import { useCallback, useEffect } from 'react';
import { BlockerFunction, useBlocker } from 'react-router';

interface UseUnsavedChangesBlockerProps {
  hasChanges: boolean;
}

const useUnsavedChangesBlocker = ({
  hasChanges,
}: UseUnsavedChangesBlockerProps) => {
  const shouldBlock = useCallback<BlockerFunction>(
    () => hasChanges,
    [hasChanges]
  );
  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (blocker.state === 'blocked' && !hasChanges) {
      blocker.proceed();
    }
  }, [blocker, hasChanges]);

  const handleSaveAndLeave = useCallback(
    async <T>(fn: () => Promise<T>) => {
      try {
        await fn();
        blocker.proceed?.();
      } catch {
        blocker.reset?.();
      }
    },
    [blocker]
  );

  const handleDiscard = useCallback(() => blocker.proceed?.(), [blocker]);

  const handleClose = useCallback(() => blocker.reset?.(), [blocker]);

  return {
    isBlocked: blocker.state === 'blocked' && hasChanges,
    handleSaveAndLeave,
    handleDiscard,
    handleClose,
  };
};

export default useUnsavedChangesBlocker;
